import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { findChildrenByType, NodeWithPos } from 'prosemirror-utils';
import { Node as ProsemirrorNode } from 'prosemirror-model';
import { EditorState, Transaction } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { Editor as TipTapViewer } from 'tiptap';
import moment from 'moment';
import { EProsemirrorType, EFieldType, EMergeKey, EMergeCategory } from '@/editor/types/enums';
import { IMergeValues, IField, ITextContentDoc } from '@/editor/types';

@Component
export class ProsemirrorViewerRoot extends Vue {
  @Prop() content: ITextContentDoc;
  @Prop() mergeValues: IMergeValues;
  @Prop() fields: IField[];
  @Watch('fields')
  @Watch('mergeValues')
  updateMergeValues() {
    this.viewer.setContent(this.content);
    this.viewerView?.dispatch(this.replaceMergeTagsWithText(this.viewerState));
  }
  @Watch('content') setContent() {
    if (JSON.stringify(this.viewer.getJSON()) === JSON.stringify(this.content)) {
      return;
    }
    this.viewer.setContent(this.content);
    this.viewerView?.dispatch(this.replaceMergeTagsWithText(this.viewerState));
  }

  viewer: TipTapViewer | null = null;

  get viewerState(): EditorState {
    return this.viewer && this.viewer.view && this.viewer.view.state;
  }

  get viewerView(): EditorView {
    return this.viewer && this.viewer.view;
  }

  handleInit({ view, state }: { view: EditorView; state: EditorState }) {
    view.dispatch(this.replaceMergeTagsWithText(state));
  }

  replaceMergeTagsWithText(state: EditorState): Transaction {
    const nodes: NodeWithPos[] = findChildrenByType(
      state.doc,
      state.schema.nodes[EProsemirrorType.MergeTag]
    ).reverse();

    let transaction = state.tr;

    nodes.forEach(({ node, pos }: NodeWithPos) => {
      const mergeValue = this.getMergeValue(node);

      if (mergeValue) {
        transaction = transaction.replaceWith(
          pos,
          pos + node.nodeSize,
          state.schema.text(mergeValue, node.marks)
        );
      }
    });

    return transaction;
  }

  getTagById(tagId: string): IField | undefined {
    return this.fields.find(({ id }: IField) => id === tagId);
  }

  getRecipientValue(recipientId: string, mergeKey: EMergeKey): string {
    const recipient = this.mergeValues?.recipients?.find(({ id }) => recipientId === id);

    if (!recipient || !recipient?.email) {
      return '';
    }

    return recipient?.[mergeKey] || '';
  }

  getEntityValue(mergeKey: EMergeKey): string {
    return this.mergeValues?.entity?.[mergeKey] || '';
  }

  getSenderValue(mergeKey: EMergeKey): string {
    return this.mergeValues?.sender?.[mergeKey] || '';
  }

  getDocumentValue(mergeKey: EMergeKey): string {
    const value: string = this.mergeValues?.document?.[mergeKey];

    if (!value) {
      return '';
    }

    if (mergeKey === EMergeKey.sendDate || mergeKey === EMergeKey.expirationDate) {
      return moment(value).format('LLL');
    }

    return String(value);
  }

  getMergeValue(node: ProsemirrorNode): string {
    const { tagId, category, mergeKey } = node.attrs as {
      tagId: string;
      category: EMergeCategory;
      mergeKey: EMergeKey;
    };

    const currentTag: IField = this.getTagById(tagId);

    if (currentTag?.type === EFieldType.Custom) {
      return currentTag.value || '';
    }

    switch (category) {
      case EMergeCategory.Recipient:
        return this.getRecipientValue(currentTag.recipientId, mergeKey);

      case EMergeCategory.Entity:
        return this.getEntityValue(mergeKey);

      case EMergeCategory.Sender:
        return this.getSenderValue(mergeKey);

      case EMergeCategory.Document:
        return this.getDocumentValue(mergeKey);

      default:
        return '';
    }
  }
}
